import { noop } from 'lodash-es'
import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { match } from 'ts-pattern'
import { openSignModalRAC } from 'src/components/Account/SignModalRAC'
import { useJsBridgeContext } from 'src/components/jsbridge/JsBridgeContext'
import { mapTierNumberToMobileProductId } from 'src/features/Membership/utils/mobileProductUtils'
import type { Currency, Interval } from 'src/services/apollo/generated/graphql'
import type { MembershipPlan } from 'src/services/helpers/membership'
import { useMembershipActions } from 'src/services/helpers/membership'
import { invokeProductPurchase } from 'src/utils/jsBridge/implementation'
import { track } from 'src/utils/tracker'
import { MembershipCardState, MembershipCardStateAction } from './stateMachine'

export interface SubscriptionInfo {
  planId: string
  tier: number
  interval: Interval
  prices: Record<Currency, number>
  oneTimeMembership?: MembershipPlan
  isActive: boolean
  shouldDiscount?: boolean
  trackingOpts?: {
    upgradeFromTier?: number
    discountType?: string
  }
  extraParamsForSubscribeRedirection?: Record<string, any>
  afterCancelSurvey?: () => any
  subscription?: {
    invoice?: {
      hostedInvoiceUrl?: string
    }
  }
}

export function useMembershipCardActions() {
  const { subscribe, unsubscribe } = useMembershipActions()
  const navigate = useNavigate()
  const { platform } = useJsBridgeContext()

  const proceedSubscribe = useCallback(
    async (
      subscriptionInfo: SubscriptionInfo,
      options?: { isNewSubscribe?: boolean },
    ) => {
      if (platform) {
        const productId = mapTierNumberToMobileProductId(
          subscriptionInfo.tier,
          subscriptionInfo.interval,
          platform,
        )
        if (productId) {
          return invokeProductPurchase(productId)
        }
      } else {
        return subscribe({
          isActive: subscriptionInfo.isActive,
          planId: subscriptionInfo.planId,
          oneTimeMembership: subscriptionInfo.oneTimeMembership,
          tier: subscriptionInfo.tier,
          prices: subscriptionInfo.prices,
          interval: subscriptionInfo.interval,
          isNewSubscribe: options?.isNewSubscribe,
          trackingOpts: subscriptionInfo.trackingOpts,
          shouldDiscount: subscriptionInfo.shouldDiscount,
          afterCancelSurvey: subscriptionInfo.afterCancelSurvey,
          extraParamsForSubscribeRedirection:
            subscriptionInfo.extraParamsForSubscribeRedirection,
        })
      }
    },
    [platform, subscribe],
  )

  const handlePendingPayment = useCallback(
    async (subscriptionInfo: SubscriptionInfo) => {
      const url = subscriptionInfo.subscription?.invoice?.hostedInvoiceUrl
      if (url) {
        window.open(url, '_blank')
      }
    },
    [],
  )

  const executeAction = useCallback(
    async (
      action: MembershipCardStateAction,
      state: MembershipCardState,
      subscriptionInfo: SubscriptionInfo,
    ) => {
      // Track specific events based on state (aligned with original component)
      match(state)
        .with(MembershipCardState.CURRENT_PLAN_CANCELLABLE, () =>
          track('plan_cancel_click', subscriptionInfo.trackingOpts),
        )
        .with(MembershipCardState.UPGRADE_AVAILABLE, () =>
          track('upgrade_member_click', subscriptionInfo.trackingOpts),
        )
        .with(MembershipCardState.DOWNGRADE_AVAILABLE, () =>
          track('downgrade_member_click', subscriptionInfo.trackingOpts),
        )
        .otherwise(noop)

      // Execute the action corresponding to the state machine action
      return match(action)
        .with(MembershipCardStateAction.LOGIN, () => openSignModalRAC())
        .with(MembershipCardStateAction.SUBSCRIBE, () =>
          proceedSubscribe(subscriptionInfo, { isNewSubscribe: true }),
        )
        .with(
          MembershipCardStateAction.RESUBSCRIBE,
          MembershipCardStateAction.UPGRADE,
          MembershipCardStateAction.DOWNGRADE,
          () => proceedSubscribe(subscriptionInfo),
        )
        .with(MembershipCardStateAction.PENDING_PAYMENT, () =>
          handlePendingPayment(subscriptionInfo),
        )
        .with(MembershipCardStateAction.VIEW_PLAN, () =>
          navigate('/me/credits'),
        )
        .with(MembershipCardStateAction.CANCEL, () => unsubscribe())
        .otherwise(() =>
          console.warn(`Unhandled action: ${action} in state: ${state}`),
        )
    },
    [proceedSubscribe, handlePendingPayment, navigate, unsubscribe],
  )

  return {
    executeAction,
  }
}
